import React, { useMemo, useState } from 'react'
import EditIcon from '@mui/icons-material/Edit'
import { IconButton } from '@mui/material'
import SmartMateriaUITable from '../generic/SmartMateriaUITable'
import { apiGet } from '../generic/Api_Functions'
import { useSmartTranslation } from '../generic/hooks/useSmartTranslation'
import { enumFilterOptions, systemValueLabel } from '../generic/systemValues/systemValueLabel'
import { SmartDialog } from '../generic/utilities/SmartDialog'
import ActionForm from './ActionForm'
import { FactCheckOutlined, TextSnippetOutlined } from '@mui/icons-material'
import { useAuthorization } from '../generic/hooks/useAuthorization'
import Document from '../configuration/document/Document'
import ActionExecutionDialog from './ActionExecutionDialog'

const getActionUuid = (actionRow) => actionRow?.uuid || actionRow?.qualityIncidenceActionUuid

const actionCanHaveChildren = (actionRow) => Boolean(
  (actionRow?.qualityIncidence || actionRow?.scopeDto) && actionRow?.actionParent === null && actionRow?.hasChildren
)

const actionHasParent = (actionRow) => Boolean(actionRow?.actionParent)

const getActionFormType = (actionRow, fallbackType) => {
  if (actionRow?.qualityIncidence) return 'qualityIncidence'
  if (actionRow?.scopeDto || actionRow?.qualityIncidenceScope) return 'qualityIncidenceScope'
  if (actionRow?.affected) return 'qualityIncidenceAffected'
  if (actionRow?.scopeAffected) return 'qualityIncidenceScopeAffected'
  return fallbackType
}

const getActionFormRow = (actionRow, fallbackRow) => {
  return actionRow?.qualityIncidence ||
    actionRow?.scopeDto ||
    actionRow?.qualityIncidenceScope ||
    actionRow?.affected ||
    actionRow?.scopeAffected ||
    fallbackRow
}

const normalizeActionRows = (rows) => {
  return rows.map((actionRow, index) => ({
    ...actionRow,
    id: actionRow?.id ?? getActionUuid(actionRow) ?? index
  }))
}

const normalizeActionResponse = (response) => {
  const rows = Array.isArray(response)
    ? response
    : Array.isArray(response?.content)
      ? response.content
      : []

  const normalizedRows = normalizeActionRows(rows)

  if (Array.isArray(response?.content)) {
    return {
      ...response,
      content: normalizedRows
    }
  }

  return normalizedRows
}

const getColumnFilterValue = (pagination, field) => {
  const filterIndex = Array.isArray(pagination?.columnFilters)
    ? pagination.columnFilters.indexOf(field)
    : -1

  if (filterIndex < 0 || !Array.isArray(pagination?.columnFilterValues)) return ''

  return String(pagination.columnFilterValues[filterIndex] || '').trim().toLowerCase()
}

const removeColumnFilter = (pagination, field) => {
  if (!Array.isArray(pagination?.columnFilters)) return pagination

  const filterIndex = pagination.columnFilters.indexOf(field)
  if (filterIndex < 0) return pagination

  return {
    ...pagination,
    columnFilters: pagination.columnFilters.filter((_, index) => index !== filterIndex),
    columnFilterValues: Array.isArray(pagination.columnFilterValues)
      ? pagination.columnFilterValues.filter((_, index) => index !== filterIndex)
      : pagination.columnFilterValues
  }
}

const removeSortField = (pagination, field) => {
  if (!Array.isArray(pagination?.sortKey)) return pagination

  const sortIndex = pagination.sortKey.indexOf(field)
  if (sortIndex < 0) return pagination

  return {
    ...pagination,
    sortKey: pagination.sortKey.filter((_, index) => index !== sortIndex),
    sortMode: Array.isArray(pagination.sortMode)
      ? pagination.sortMode.filter((_, index) => index !== sortIndex)
      : pagination.sortMode
  }
}

const removeLocalResourceRequestFields = (pagination) => ({
  ...removeSortField(removeColumnFilter(pagination, 'resource'), 'resource'),
  columns: Array.isArray(pagination?.columns)
    ? pagination.columns.filter((column) => column !== 'resource')
    : pagination?.columns
})

const rowMatchesResource = (actionRow, resourceFilter) => {
  if (String(actionRow?.resource || '').toLowerCase().includes(resourceFilter)) return true
  const childResources = Array.isArray(actionRow?.childResources) ? actionRow.childResources : []
  return childResources.some((code) => String(code || '').toLowerCase().includes(resourceFilter))
}

const filterActionResourcePage = (data, resourceFilter, pagination) => {
  if (!resourceFilter) return data

  const rows = Array.isArray(data)
    ? data
    : Array.isArray(data?.content)
      ? data.content
      : []

  const filteredRows = rows.filter((actionRow) => rowMatchesResource(actionRow, resourceFilter))
  const page = Math.max(0, Number(pagination?.page || 0))
  const size = Number(pagination?.size || filteredRows.length || 0)
  const pagedRows = size > 0 ? filteredRows.slice(page * size, page * size + size) : filteredRows

  if (Array.isArray(data?.content)) {
    return {
      ...data,
      content: pagedRows,
      totalElements: filteredRows.length,
      number: page,
      size
    }
  }

  return pagedRows
}

const QualityIncidenceActionChildrenTable = ({ actionUuid, tableKey, columns, actions }) => {
  const dataFetch = async (pagination) => {
    if (!actionUuid) return []
    const resourceFilter = getColumnFilterValue(pagination, 'resource')
    const requestPagination = resourceFilter ? { ...removeLocalResourceRequestFields(pagination), page: 0, size: -1 } : pagination
    const response = await apiGet('action/' + actionUuid, requestPagination)
    return filterActionResourcePage(normalizeActionResponse(response), resourceFilter, pagination)
  }

  return (
    <SmartMateriaUITable
      tableTuneUniqueKey={tableKey}
      columns={columns}
      dataFetch={dataFetch}
      actions={actions}
      pageable
      dense
      sticky={false}
      tablePageSize={5}
      toolbarDisplay='none'
      hideExport
    />
  )
}

const QualityIncidenceActionsTable = ({
  endpointType,
  formType,
  row,
  tableKey,
  onActionsChanged,
  // When true the table lists every action (global view) instead of the actions of a single incidence:
  // it fetches the global endpoint, prepends the incidence columns and hides the "add record" action.
  global = false,
  // Historia 170 (J4b, S103) — additive, optional, inert when absent (every OTHER caller):
  // `Quality.jsx` forwards this when a document repository row's "Open associated record" preselects
  // a QUALITY_INCIDENCE_ACTION inside this incidence.
  preselectedRecord = null
}) => {
  const { t_ } = useSmartTranslation()
  const [openActionForm, setOpenActionForm] = useState(false)
  const [selectedAction, setSelectedAction] = useState(null)
  const [pendingEditAction, setPendingEditAction] = useState(null)
  // Kept in state (set when the dialog opens) instead of derived from pendingEditAction, so the text does
  // not flip to the "parent" message during the dialog's close animation once pendingEditAction is cleared.
  const [editWarningMessage, setEditWarningMessage] = useState('')
  const [forceLoad, setForceLoad] = useState(false)
  const uuid = row?.uuid || row?.id
  const isUserAuthorized = useAuthorization()
  const [showDocument, setShowDocument] = useState(false)
  const [selectedRow, setSelectedRow] = useState(null)
  const [isDownloadingFile, setIsDownloadingFile] = useState(false)
  const [showExecute, setShowExecute] = useState(false)
  const [executeRow, setExecuteRow] = useState(null)

  const openEditForm = (actionRow) => {
    setSelectedAction(actionRow)
    setOpenActionForm(true)
  }

  const handleEditClick = (actionRow) => {
    if (actionHasParent(actionRow) || actionRow?.hasChildren) {
      setEditWarningMessage(
        actionHasParent(actionRow)
          ? t_('Editing this child action will unlink it from its parent action.')
          : t_('Editing this action will affect all its child actions.')
      )
      setPendingEditAction(actionRow)
      return
    }
    openEditForm(actionRow)
  }

  const columns = useMemo(() => [
    ...(global
      ? [
          {
            name: t_('Incidence code'),
            field: 'qualityIncidence.code'
          },
          {
            name: t_('Incidence title'),
            field: 'qualityIncidence.title'
          }
        ]
      : []),
    {
      name: t_('Code'),
      field: 'code'
    },
    {
      name: t_('type'),
      field: 'actionType',
      render: (row) => systemValueLabel(t_, 'QualityIncidenceActionTypeEnum', row.actionType),
      filterByLabel: { options: enumFilterOptions(t_, 'QualityIncidenceActionTypeEnum') }
    },
    {
      name: t_('status'),
      field: 'actionStatus',
      render: (row) => systemValueLabel(t_, 'QualityIncidenceActionStatusEnum', row.actionStatus),
      filterByLabel: { options: enumFilterOptions(t_, 'QualityIncidenceActionStatusEnum') }
    },
    {
      name: t_('Result'),
      field: 'actionResult',
      render: (row) => systemValueLabel(t_, 'QualityIncidenceActionResultEnum', row.actionResult),
      filterByLabel: { options: enumFilterOptions(t_, 'QualityIncidenceActionResultEnum') }
    },
    {
      name: t_('Execution comment'),
      field: 'executionComment'
    },
    {
      name: t_('Description'),
      field: 'description'
    },
    {
      name: t_('Fabrication Order'),
      field: 'fabricationOrder'
    },
    {
      name: t_('Job'),
      field: 'job'
    },
    {
      name: t_('resource'),
      field: 'resource'
    },
    {
      name: t_('Preliminary analysis'),
      field: 'qualityIncidence.preliminaryAnalysis'
    },
    {
      name: t_('Expiration date'),
      field: 'expirationDate',
      type: 'datetime'
    },
    {
      name: t_('Responsible'),
      field: 'responsible.name'
    },
    {
      name: t_('Notification sent'),
      field: 'notificationSent',
      type: 'boolean',
      hide: true
    }
  ], [t_, global])

  const childColumns = useMemo(() => [
    {
      name: t_('status'),
      field: 'actionStatus',
      render: (row) => systemValueLabel(t_, 'QualityIncidenceActionStatusEnum', row.actionStatus),
      filterByLabel: { options: enumFilterOptions(t_, 'QualityIncidenceActionStatusEnum') }
    },
    {
      name: t_('Result'),
      field: 'actionResult',
      render: (row) => systemValueLabel(t_, 'QualityIncidenceActionResultEnum', row.actionResult),
      filterByLabel: { options: enumFilterOptions(t_, 'QualityIncidenceActionResultEnum') }
    },
    {
      name: t_('Execution comment'),
      field: 'executionComment'
    },
    {
      name: t_('Fabrication Order'),
      field: 'fabricationOrder'
    },
    {
      name: t_('Job'),
      field: 'job'

    },
    {
      name: t_('resource'),
      field: 'resource'
    },
    {
      name: t_('Expiration date'),
      field: 'expirationDate',
      type: 'datetime'
    }
  ], [t_])

  const actions = {
    ...(global ? {} : { create: { label: 'ADD RECORD' } }),
    custom: [
      {
        name: 'Edit',
        onClick: (row) => {
          handleEditClick(row)
        },
        render: (actionRow) => (
          <IconButton
            size='small'
          >
            <EditIcon fontSize='small' />
          </IconButton>
        )
      },
      {
        name: 'Documents',
        onClick: (row) => { setShowDocument(true); setSelectedRow(row) },
        render: (row) => {
          return (
            <IconButton size='small'>
              <TextSnippetOutlined fontSize='small' />
            </IconButton>
          )
        },
        disabled: (row) => !isUserAuthorized('DOCUMENTS_QUALITY_INCIDENCE')
      },
      {
        name: 'Execute action',
        onClick: (row) => { setExecuteRow(row); setShowExecute(true) },
        render: () => (
          <IconButton size='small'>
            <FactCheckOutlined fontSize='small' />
          </IconButton>
        ),
        // A parent action only groups children; execution (accept/reject) is done on each leaf action,
        // so the option is hidden for parent rows.
        hide: (row) => Boolean(row?.hasChildren),
        disabled: (row) => !getActionUuid(row)
      }
    ],
    accordion: {
      // visibilityButton (instead of hide) keeps the icon's column slot reserved with
      // `visibility: hidden` when the row has no children, so the kebab menu stays aligned
      // across rows. The tooltip also has to be silenced for those rows — MUI's Tooltip still
      // fires on hover even when its child is `visibility: hidden`.
      visibilityButton: (actionRow) => Boolean(getActionUuid(actionRow) && actionCanHaveChildren(actionRow)),
      tooltip: (actionRow) => (
        getActionUuid(actionRow) && actionCanHaveChildren(actionRow) ? t_('Affected resources') : ''
      ),
      render: (actionRow) => {
        const childActionUuid = getActionUuid(actionRow)
        return (
          <QualityIncidenceActionChildrenTable
            actionUuid={childActionUuid}
            tableKey={`${tableKey || 'qualityIncidenceAction'}Children${childActionUuid || ''}`}
            columns={childColumns}
            actions={actions}
          />
        )
      }
    }
  }

  const dataFetch = async (pagination) => {
    if (!global && (!endpointType || !uuid)) return []
    const resourceFilter = getColumnFilterValue(pagination, 'resource')
    const requestPagination = resourceFilter ? { ...pagination, page: 0, size: -1 } : pagination
    const endpoint = global ? 'qualityIncidenceAction' : 'action/' + endpointType + '/' + uuid
    const response = await apiGet(endpoint, requestPagination)
    return filterActionResourcePage(normalizeActionResponse(response), resourceFilter, pagination)
  }

  const handleSaved = () => {
    setForceLoad((previous) => !previous)
    onActionsChanged?.()
  }

  const editActionType = selectedAction ? getActionFormType(selectedAction, formType) : formType
  const editActionRow = selectedAction ? getActionFormRow(selectedAction, row) : row

  return (
    <>
      <SmartDialog
        closeCallback={() => setShowDocument(false)}
        accept={false}
        cancel={false}
        close
        disableButtons={isDownloadingFile}
        renderComponent={
          <Document
            reference={{ id: selectedRow?.id, code: selectedRow?.code }}
            resourceType='QUALITY_INCIDENCE_ACTION'
            isDownloadingFile={isDownloadingFile}
            setIsDownloadingFile={setIsDownloadingFile}
            defaultName={selectedRow?.code}
          />
        }
        setOpen={showDocument}
      />
      <SmartDialog
        closeCallback={() => setShowExecute(false)}
        accept={false}
        cancel={false}
        close
        setOpen={showExecute}
        renderComponent={
          <ActionExecutionDialog
            key={getActionUuid(executeRow) || 'none'}
            action={executeRow}
            onClose={() => setShowExecute(false)}
            onExecuted={handleSaved}
          />
        }
      />
      <SmartDialog
        title='default'
        message={editWarningMessage}
        setOpen={Boolean(pendingEditAction)}
        close={false}
        accept
        cancel
        acceptLabel='Continue'
        cancelLabel='Cancel'
        acceptCallback={() => {
          openEditForm(pendingEditAction)
          setPendingEditAction(null)
        }}
        cancelCallback={() => setPendingEditAction(null)}
      />
      <SmartDialog
        closeCallback={() => {
          setOpenActionForm(false)
        }}
        accept={false}
        cancel={false}
        close
        setOpen={openActionForm}
        renderComponent={
          <ActionForm
            type={editActionType}
            row={editActionRow}
            action={selectedAction}
            unlinkParentOnSave={actionHasParent(selectedAction)}
            onClose={() => {
              setOpenActionForm(false)
            }}
            onSaved={handleSaved}
          />
        }
      />
      <SmartMateriaUITable
        tableTuneUniqueKey={tableKey}
        title={t_('Quality incidence actions')}
        columns={columns}
        dataFetch={dataFetch}
        actions={actions}
        pageable
        preselectedRecord={preselectedRecord}
        forceLoad={forceLoad}
        dense
        {...(global
          ? {}
          : {
              onAddElementClick: () => {
                setSelectedAction(null)
                setOpenActionForm(true)
              },
              customAddRecordClick: true,
              sticky: false,
              tablePageSize: 5,
              toolbar: {
                render: {
                  fetchFunction: () => null
                }
              },
              hideExport: true
            })}
      />
    </>
  )
}

export default QualityIncidenceActionsTable
